'use client';

import * as React from 'react';
import { Menu } from '@base-ui/react/menu';
import { clsx } from 'clsx';
import styles from './SplitButton.module.css';

export interface SplitButtonItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** Optional icon shown before the label (e.g. a checkmark for the current selection) */
  icon?: React.ReactNode;
}

export interface SplitButtonProps {
  /** Label for the primary action button */
  label: string;
  /** Called when the primary (label) button is clicked */
  onClick?: () => void;
  /** Optional icon to display before the label */
  startIcon?: React.ReactNode;
  /** Items shown in the dropdown menu */
  items: SplitButtonItem[];
  variant?: 'primary' | 'secondary' | 'accent';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  /** Stretch to fill the width of its container */
  fullWidth?: boolean;
  /** Clicking the main (label) button also opens the dropdown menu, instead of only running `onClick` */
  mainOpensMenu?: boolean;
  className?: string;
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

export const SplitButton = React.forwardRef<HTMLDivElement, SplitButtonProps>(
  (
    {
      label,
      onClick,
      startIcon,
      items,
      variant = 'primary',
      size = 'md',
      disabled = false,
      fullWidth = false,
      mainOpensMenu = false,
      className,
    },
    ref
  ) => {
    // Base UI anchors the popup to whichever trigger was clicked, so align="end"
    // would hug the main button's right edge when it opens the menu. Anchoring to
    // the root keeps the menu flush with the chevron either way.
    const rootRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => rootRef.current as HTMLDivElement);

    const mainContent = (
      <>
        {startIcon && <span className={styles.icon}>{startIcon}</span>}
        {label}
      </>
    );

    return (
      <div
        ref={rootRef}
        className={clsx(
          styles.root,
          styles[variant],
          styles[size],
          disabled && styles.disabled,
          fullWidth && styles.fullWidth,
          className
        )}
      >
        {/* `modal` defaults to true, which layers a second scroll lock and a
            full-viewport backdrop when this renders inside an already-modal
            Dialog/SideSheet. Every other menu in the app opts out the same way. */}
        <Menu.Root modal={false}>
          {mainOpensMenu ? (
            <Menu.Trigger className={styles.mainBtn} disabled={disabled}>
              {mainContent}
            </Menu.Trigger>
          ) : (
            <button
              type="button"
              className={styles.mainBtn}
              onClick={onClick}
              disabled={disabled}
            >
              {mainContent}
            </button>
          )}

          <Menu.Trigger
            className={styles.chevronBtn}
            disabled={disabled}
            aria-label="More options"
          >
            <ChevronDownIcon />
          </Menu.Trigger>

          <Menu.Portal>
            {/* positionMethod="fixed" because SideSheet's popup carries a
                transform, which would otherwise become the containing block. */}
            <Menu.Positioner
              className={styles.positioner}
              anchor={rootRef}
              side="bottom"
              align="end"
              sideOffset={4}
              collisionPadding={8}
              positionMethod="fixed"
            >
              <Menu.Popup className={styles.popup}>
                {items.map((item) => (
                  <Menu.Item
                    key={item.label}
                    className={clsx(styles.item, item.destructive && styles.itemDestructive)}
                    disabled={item.disabled}
                    onClick={item.onClick}
                  >
                    <span>{item.label}</span>
                    {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
                  </Menu.Item>
                ))}
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      </div>
    );
  }
);

SplitButton.displayName = 'SplitButton';

export default SplitButton;
